"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import useTranslation from "@/hook/useTranslation";
import moment from "moment";
import "./TopNewsSidebar.css";

const placeholderImage =
  "https://dummyimage.com/300x200/cccccc/ffffff&text=No+Image";

const mockNews = Array.from({ length: 4 }, (_, index) => ({
  id: `mock-${index + 1}`,
  title: `Mock News ${index + 1}`,
  description: `This is a description for mock news ${index + 1}.`,
  image_url: placeholderImage,
  url: "#",
  published_at: moment().subtract(index, "hours").format("YYYYMMDDTHHmmss"),
  category: ["Forex", "Crypto", "Markets", "Business"][index % 4],
}));

export default function TopNewsSidebar() {
  const router = useRouter();
  const { locale, t } = useTranslation();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchNews() {
      try {
        const response = await fetch(
          `https://www.alphavantage.co/query?function=NEWS_SENTIMENT&apikey=YOUR_API_KEY&topics=forex,finance,economy&limit=50`
        );
        const result = await response.json();
        const apiNews = (result.feed || []).map((item, idx) => ({
          id: `api-${idx}`,
          title: item.title,
          description: item.summary || "No description",
          image_url: item.banner_image || placeholderImage,
          url: item.url,
          published_at: item.time_published,
          category: item.topic || "Other",
        }));

        setNews(apiNews.length > 0 ? apiNews : mockNews);
      } catch (e) {
        console.error("API failed. Using mock data instead.", e);
        setNews(mockNews);
      } finally {
        setLoading(false);
      }
    }

    fetchNews();
  }, []);

  useEffect(() => {
    const syncHeight = () => {
      const left = document.querySelector(".left-grid");
      const right = document.querySelector(".topnews-container");

      if (window.innerWidth >= 1024 && left && right) {
        right.style.height = `${left.clientHeight}px`;
      } else if (right) {
        right.style.height = "auto";
      }
    };

    syncHeight();
    window.addEventListener("resize", syncHeight);
    return () => window.removeEventListener("resize", syncHeight);
  }, []);

  const sortedNews = [...news].sort(
    (a, b) => new Date(b.published_at) - new Date(a.published_at)
  );
  const top4 = sortedNews.slice(0, 4);

  if (loading) {
    return (
      <div className="topnews-container adjusted-height">
        <p className="loading">Loading hot news...</p>
      </div>
    );
  }

  return (
    <div className="topnews-container adjusted-height">
      <div className="topnews-header">
        <h2 className="topnews-title">
          <span className="topnews-slash">&#47;&#47;</span>
          {t("24-hour hot list") || "24-hour hot list"}
        </h2>
        <a href={`/${locale}/news`} className="topnews-viewmore bold-visible">
          {t("View more lists") || "View more lists"}
        </a>
      </div>

      <div className="topnews-flex">
        <div className="topnews-featured">
          {top4.slice(0, 2).map((item, index) => (
            <div
              key={item.id}
              className="topnews-item hoverable"
              onClick={() => router.push(`/${locale}/news/${item.id}`)}
            >
              <div className="topnews-image-wrapper">
                <span
                  className={`topnews-rank ${index === 0 ? "red" : "orange"}`}
                >
                  TOP{index + 1}
                </span>
                <Image
                  src={item.image_url || placeholderImage}
                  alt={item.title}
                  width={300}
                  height={180}
                  className="topnews-image"
                  unoptimized
                />
              </div>
              <p className="topnews-text">{item.title}</p>
            </div>
          ))}
        </div>

        <ul className="topnews-list">
          {top4.slice(2).map((item, i) => (
            <li
              key={item.id}
              className="topnews-list-item hoverable"
              onClick={() => router.push(`/${locale}/news/${item.id}`)}
            >
              <Image
                src={item.image_url || placeholderImage}
                alt={item.title}
                width={64}
                height={64}
                className="topnews-thumb"
                unoptimized
              />
              <div className="topnews-list-content">
                <span className="topnews-index">{i + 3}</span>
                <p className="topnews-title-small">{item.title}</p>
                <p className="topnews-time">
                  {moment(item.published_at, "YYYYMMDDTHHmmss").fromNow()}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
